import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { LocationOnOutlined, GetApp } from '@material-ui/icons'

import { Input, Button } from '@baseV2'
import { displayAmount, allowNumericKeyDown } from '@utils'
import { strings } from '@constants'
import { Spacings, Text } from '@styles'

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	flex-wrap: wrap;
`

const SectionLabel = styled.div`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_700};
	letter-spacing: 0.03em;
	text-transform: uppercase;
	color: ${({ theme }) => theme.colors.text.secondary};
`

const TotalAttached = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.brand.primary};
`

const EmptyState = styled.div`
	padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
	text-align: center;
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
	border: 1px dashed ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
`

const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	margin-top: ${Spacings.SPACING_3B};
`

const Card = styled.div`
	border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.brand.primary : theme.colors.border.default)};
	border-radius: ${({ theme }) => theme.radius.md};
	padding: ${Spacings.SPACING_3B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	background: ${({ theme, $active }) => ($active ? theme.colors.brand.subtle : theme.colors.surface.card)};
`

const TopRow = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	flex-wrap: wrap;
`

const InvoiceNo = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
`

const DueDate = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const Stat = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	background: transparent;
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	padding: ${Spacings.SPACING_2B};
`

const StatLabel = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const StatValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme, $highlight }) => ($highlight ? theme.colors.brand.primary : theme.colors.text.primary)};
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
`

const ErrorText = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.colors.status.error.text};
`

const RightMeta = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	flex-wrap: wrap;
`

const StatusPill = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_600};
	text-transform: uppercase;
	letter-spacing: 0.02em;
	padding: ${Spacings.SPACING_1} ${Spacings.SPACING_2B};
	border-radius: ${({ theme }) => theme.radius.sm};
	color: ${({ theme, $delivered }) => ($delivered ? theme.colors.status.success.text : theme.colors.text.secondary)};
	background: transparent;
`

const DocLink = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.brand.primary};
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`

// --- Document preview modal ---
const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.55);
	padding: ${Spacings.SPACING_4B};
`

const ModalCard = styled.div`
	background: ${({ theme }) => theme.colors.surface.card};
	border-radius: ${({ theme }) => theme.radius.md};
	width: min(900px, 100%);
	height: min(90vh, 100%);
	display: flex;
	flex-direction: column;
	overflow: hidden;
`

const ModalHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

const ModalTitle = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
`

const ModalActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
`

const ModalClose = styled.button`
	border: none;
	background: transparent;
	font-size: ${Text.X_LARGE};
	line-height: 1;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.text.secondary};
`

const ModalBody = styled.div`
	flex: 1;
	overflow: auto;
	background: ${({ theme }) => theme.colors.surface.sunken};
	display: flex;
	align-items: center;
	justify-content: center;

	& > iframe,
	& > img {
		width: 100%;
		height: 100%;
		border: none;
	}

	& > img {
		object-fit: contain;
	}
`

const isImageUrl = (url = '') => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(url)

const ModalMessage = styled.div`
	padding: ${Spacings.SPACING_6B};
	text-align: center;
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
`

// Infer a viewable MIME type from the URL extension so the blob renders inline
// (instead of the server's Content-Disposition: attachment forcing a download).
const inferMimeType = (url = '') => {
	const ext = (url.split(/[?#]/)[0].split('.').pop() || '').toLowerCase()
	const map = {
		pdf: 'application/pdf',
		png: 'image/png',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		gif: 'image/gif',
		webp: 'image/webp',
		bmp: 'image/bmp',
		svg: 'image/svg+xml',
	}
	return map[ext] || 'application/pdf'
}

const ShippingText = styled.div`
	display: flex;
	align-items: flex-start;
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.SMALL};
	line-height: ${Text.X_LARGE};
	color: ${({ theme }) => theme.colors.text.secondary};
`

// Amount input · [Balance Due + Apply] · Untagged Amount — one row, equal-height cells.
const InputRow = styled.div`
	display: flex;
	align-items: stretch;
	gap: ${Spacings.SPACING_2B};
	flex-wrap: wrap;

	/* Amount input: compact, and bottom-aligned with the stat cells beside it. */
	& > div:first-child {
		flex: 1 1 7rem;
		max-width: 11rem;
		min-width: 6rem;
		align-self: flex-end;
	}
`

// Address row with the Invoice Amount stat on its right.
const AddressRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
	flex-wrap: wrap;

	& > *:first-child {
		flex: 1 1 10rem;
		min-width: 0;
	}

	/* Invoice Amount sits snug on the right instead of stretching. */
	& > *:last-child {
		flex: 0 0 auto;
	}
`

// Compact grey stat used beside the address and inside the input row.
const InlineStat = styled(Stat)`
	flex: 1 1 8rem;
	min-width: 7rem;
	max-width: 14rem;
	justify-content: center;
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
`

// Balance Due and its Apply action share one cell: figures on the left, button on the right.
const BalanceDueCell = styled(InlineStat)`
	flex: 1 1 14rem;
	max-width: 20rem;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} ${Spacings.SPACING_3B};
`

const StatBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	min-width: 0;
`

// Apply sits inside the Balance Due cell, so keep it compact and unshrunk.
const ApplyButton = styled(Button)`
	flex: 0 0 auto;
	white-space: nowrap;
`

// Presentational list of due/overdue invoices with per-invoice allocation inputs + Auto-Fill.
const AttachInvoices = ({ invoices, allocations, allocErrors, attached, advance, onAllocationChange, disabled = false }) => {
	const [search] = useState('') // search box is commented out below
	// { url, title } of the document currently previewed in the modal, or null.
	const [preview, setPreview] = useState(null)
	// blob object-URL for the fetched doc (bypasses download-forcing headers), + fetch status.
	const [blobUrl, setBlobUrl] = useState(null)
	const [previewStatus, setPreviewStatus] = useState('idle') // idle | loading | ready | error

	// Fetch the document as a blob and render it inline; the server sends
	// Content-Disposition: attachment, so pointing an iframe straight at the URL
	// downloads instead of previewing. Re-tagging the blob with a viewable MIME
	// type and using its object-URL forces inline rendering.
	useEffect(() => {
		if (!preview?.url) return undefined
		let objectUrl
		let cancelled = false
		setPreviewStatus('loading')
		setBlobUrl(null)
		fetch(preview.url)
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`)
				return res.blob()
			})
			.then((blob) => {
				if (cancelled) return
				const typed = new Blob([blob], { type: inferMimeType(preview.url) })
				objectUrl = URL.createObjectURL(typed)
				setBlobUrl(objectUrl)
				setPreviewStatus('ready')
			})
			.catch(() => {
				if (!cancelled) setPreviewStatus('error')
			})
		return () => {
			cancelled = true
			if (objectUrl) URL.revokeObjectURL(objectUrl)
		}
	}, [preview])

	// Matches the invoice number (original_invoice_no) as well as the transaction number.
	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		if (!q) return invoices
		return invoices.filter((inv) =>
			[inv.number, inv.transactionNo, inv.id].some((field) => String(field ?? '').toLowerCase().includes(q))
		)
	}, [invoices, search])

	if (!invoices || invoices.length === 0) {
		return (
			<div>
				<SectionLabel>{strings('attach', 'invoices')}</SectionLabel>
				<List>
					<EmptyState>No Pending Invoices</EmptyState>
				</List>
			</div>
		)
	}

	return (
		<div>
			<HeaderRow>
				<SectionLabel>{strings('attach', 'invoices')}</SectionLabel>
				{/* <TotalAttached>Total Attached: {displayAmount(attached)}</TotalAttached> */}
			</HeaderRow>

			{/* Search hidden for now — behaviour wasn't right.
			<div style={{ marginTop: Spacings.SPACING_3B }}>
				<Input
					placeholder='Search invoice / transaction number'
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>
			*/}

			<List>
				{filtered.map((inv) => {
					const allocated = allocations[inv.id]
					const isActive = allocated !== '' && allocated != null && Number(allocated) > 0
					return (
						<Card key={inv.id} $active={isActive}>
							<TopRow>
								<InvoiceNo>{inv.number}</InvoiceNo>
								<RightMeta>
									{inv.docUrl && (
										<DocLink
											role='button'
											tabIndex={0}
											title='Download document'
											onClick={() => setPreview({ url: inv.docUrl, title: inv.number })}
										>
											<GetApp fontSize='small' />
										</DocLink>
									)}
									{inv.deliveryStatus && (
										<StatusPill $delivered={String(inv.deliveryStatus).toLowerCase().includes('deliver')}>
											{inv.deliveryStatus}
										</StatusPill>
									)}
									<DueDate>Due: {inv.dueDateLabel}</DueDate>
								</RightMeta>
							</TopRow>
							{/* Address on the left, Invoice Amount on its right. */}
							<AddressRow>
								{inv.shippingLocation ? (
									<ShippingText>
										<LocationOnOutlined style={{ fontSize: '1rem', flexShrink: 0 }} />
										<span>{inv.shippingLocation}</span>
									</ShippingText>
								) : (
									<span />
								)}
								<InlineStat>
									<StatLabel>Invoice Amount</StatLabel>
									<StatValue>{displayAmount(inv.invoiceAmount)}</StatValue>
								</InlineStat>
							</AddressRow>

							{/* Amount input · [Balance Due + Apply] · Untagged Amount */}
							<InputRow>
								<Input
									type='number'
									min='0'
									placeholder='0'
									leftIcon='₹'
									value={allocated ?? ''}
									onKeyDown={allowNumericKeyDown}
									onChange={(e) => onAllocationChange(inv, e.target.value)}
									disabled={disabled}
								/>
								<BalanceDueCell>
									<StatBody>
										<StatLabel>Balance Due</StatLabel>
										<StatValue $highlight>{displayAmount(inv.balanceDue)}</StatValue>
									</StatBody>
									<ApplyButton
										size='small'
										color='primary'
										disabled={disabled || Number(advance) <= 0}
										title='Allocate the available advance against this invoice'
										onClick={() => onAllocationChange(inv, String(inv.balanceDue))}
									>
										Apply
									</ApplyButton>
								</BalanceDueCell>
								<InlineStat>
									<StatLabel>Untagged Amount</StatLabel>
									<StatValue>{displayAmount(Math.max(0, Number(inv.balanceDue || 0) - Number(allocated || 0)))}</StatValue>
								</InlineStat>
							</InputRow>
							{allocErrors?.[inv.id] && <ErrorText>{allocErrors[inv.id]}</ErrorText>}
						</Card>
					)
				})}
				{filtered.length === 0 && <EmptyState>No invoices match your search</EmptyState>}
			</List>

			{preview && (
				<ModalOverlay onClick={() => setPreview(null)}>
					<ModalCard onClick={(e) => e.stopPropagation()}>
						<ModalHead>
							<ModalTitle>{preview.title || 'Document'}</ModalTitle>
							<ModalActions>
								<ModalClose type='button' aria-label='Close preview' onClick={() => setPreview(null)}>
									×
								</ModalClose>
							</ModalActions>
						</ModalHead>
						<ModalBody>
							{previewStatus === 'loading' && <ModalMessage>Loading preview…</ModalMessage>}
							{previewStatus === 'error' && <ModalMessage>Preview not available for this document.</ModalMessage>}
							{previewStatus === 'ready' &&
								blobUrl &&
								(isImageUrl(preview.url) ? (
									<img src={blobUrl} alt={preview.title || 'Document preview'} />
								) : (
									<iframe src={blobUrl} title={preview.title || 'Document preview'} />
								))}
						</ModalBody>
					</ModalCard>
				</ModalOverlay>
			)}
		</div>
	)
}

export default AttachInvoices
